import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { Certificate as CertIcon, Detective } from "@phosphor-icons/react";
import { toast } from "sonner";
import RichText from "../../components/RichText";
import PlagiarismCheckModal from "../../components/PlagiarismCheckModal";
import { MediaRenderer } from "../../components/MediaAttachments";
import { useAuth } from "../../lib/auth";

export default function Results() {
  const { aid } = useParams();
  const { user } = useAuth();
  const isStaff = user && user.role !== "student";
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cert, setCert] = useState(null);
  const [issuingCert, setIssuingCert] = useState(false);
  const [plagQ, setPlagQ] = useState(null);
  const [reviewReq, setReviewReq] = useState(null);   // student's own request (null = none)
  const [reqBusy, setReqBusy] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonText, setReasonText] = useState("");

  const loadEverything = async () => {
    try {
      const a = await api.get(`/attempts/${aid}`);
      setAttempt(a.data);
      const [qs, lb, rq] = await Promise.all([
        api.get(`/attempts/${aid}/questions`),
        api.get(`/exams/${a.data.exam_id}/leaderboard`),
        api.get(`/attempts/${aid}/review-request`).catch(() => ({ data: null })),
      ]);
      setQuestions(qs.data);
      setLeaderboard(lb.data);
      setReviewReq(rq.data || null);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadEverything(); }, [aid]);

  const submitReviewRequest = async () => {
    setReqBusy(true);
    try {
      const r = await api.post(`/attempts/${aid}/review-request`, { reason: reasonText.trim() });
      setReviewReq(r.data);
      setReasonModalOpen(false);
      setReasonText("");
      toast.success("Review request sent to admin");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    } finally { setReqBusy(false); }
  };

  if (loading) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (!attempt || !attempt.result) return <div className="text-sm text-zinc-500">Result not available</div>;

  const r = attempt.result;
  const subjectData = Object.entries(r.subject_breakdown || {}).map(([sid, v]) => ({
    subject: sid.replace("subj_", "").replace(/^./, (c) => c.toUpperCase()),
    Score: v.score,
    Total: v.total,
  }));

  const passColor = r.passed ? "text-green-700 bg-green-50 border-green-600" : "text-red-700 bg-red-50 border-red-600";
  const myRank = leaderboard.find((l) => l.user_id === attempt.user_id);

  // Staff viewing another student's attempt should NOT see the "Get Certificate" CTA
  // (backend forbids issuing on behalf of another user — keep UI honest)
  const isOwnAttempt = !!user && user.user_id === attempt.user_id;
  const canIssueCert = r.passed && isOwnAttempt;

  const issueCert = async () => {
    setIssuingCert(true);
    try {
      const res = await api.post(`/certificates/${aid}/issue`);
      setCert(res.data);
      toast.success("Certificate issued");
      window.open(`${API_BASE}/certificates/${res.data.cert_id}/download`, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not issue certificate");
    } finally {
      setIssuingCert(false);
    }
  };

  return (
    <div>
      <PageHeader
        overline="Result Sheet"
        title={attempt.exam_title || "Exam Result"}
        subtitle={`Submitted ${new Date(attempt.submitted_at).toLocaleString()}`}
        action={
          <div className="flex items-center gap-2">
            {canIssueCert && (
              <button
                onClick={issueCert}
                disabled={issuingCert}
                className="bg-zinc-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-zinc-800 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="issue-certificate-btn"
              >
                <CertIcon size={14} weight="fill" />
                {issuingCert ? "Issuing…" : cert ? "Download again" : "Get Certificate"}
              </button>
            )}
            <Link to="/app/results" className="text-xs link-underline font-medium" data-testid="back-to-results">← All results</Link>
          </div>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 border border-zinc-200 mb-10" data-testid="results-summary">
        <SummaryCell label="Score" value={`${r.score} / ${r.total_marks}`} />
        <SummaryCell label="Percentage" value={`${r.percentage}%`} />
        <SummaryCell label="Rank" value={myRank ? `#${myRank.rank}` : "—"} />
        <SummaryCell label="Correct / Wrong" value={`${r.correct_count} / ${r.wrong_count}`} />
        <SummaryCell label="Status" value={r.passed ? "Pass" : "Fail"} colorClass={passColor} />
      </div>

      {/* Subject breakdown */}
      <div className="grid lg:grid-cols-3 gap-0 border border-zinc-200 mb-10">
        <div className="lg:col-span-2 p-6 border-r border-zinc-200">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-6 font-semibold">Subject-wise Performance</div>
          {subjectData.length === 0 ? (
            <div className="text-sm text-zinc-500">No subject breakdown.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={subjectData}>
                <XAxis dataKey="subject" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} />
                <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #e4e4e7" }} />
                <Bar dataKey="Total" fill="#e4e4e7" />
                <Bar dataKey="Score" fill="#09090b">
                  {subjectData.map((_, i) => <Cell key={i} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="p-6">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Leaderboard</div>
          <div className="space-y-2">
            {leaderboard.slice(0, 10).map((l) => (
              <div
                key={l.user_id}
                className={`flex items-center justify-between px-3 py-2 text-sm ${l.user_id === attempt.user_id ? "bg-zinc-900 text-white" : "bg-zinc-50"}`}
                data-testid={`leaderboard-row-${l.rank}`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono-tab text-xs w-6">#{l.rank}</span>
                  <span className="font-medium">{l.name}</span>
                </div>
                <span className="font-mono-tab font-semibold">{l.score}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed review — gated behind admin approval for students */}
      <ReviewGate
        isStaff={isStaff}
        reviewReq={reviewReq}
        onRequest={() => setReasonModalOpen(true)}
      >
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Detailed review</div>
          <div className="border border-zinc-200">
            {r.detail.map((d, i) => {
              const q = questions.find((x) => x.question_id === d.question_id);
              if (!q) return null;
              return (
                <div key={d.question_id} className={`p-5 ${i < r.detail.length - 1 ? "border-b border-zinc-200" : ""}`} data-testid={`review-q-${i + 1}`}>
                  <div className="flex items-start gap-4">
                    <div className={`flex items-center justify-center min-w-8 h-8 text-xs font-bold ${d.is_correct ? "bg-green-600 text-white" : d.user_answer.length === 0 ? "bg-zinc-200 text-zinc-600" : "bg-red-600 text-white"}`}>
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium"><RichText html={q.text} /></div>
                      {q.attachments && q.attachments.length > 0 && (
                        <div className="mt-2"><MediaRenderer attachments={q.attachments} inline /></div>
                      )}
                      {isStaff && q.type === "short_answer" && (
                        <button
                          onClick={() => setPlagQ(q.question_id)}
                          className="mt-2 text-xs text-zinc-700 hover:text-zinc-900 underline inline-flex items-center gap-1"
                          data-testid={`plag-check-${q.question_id}`}
                        >
                          <Detective size={12} weight="fill" /> Run plagiarism check on this question
                        </button>
                      )}
                      <div className="mt-2 text-xs space-y-1">
                        <div className="text-zinc-600">Your answer: <span className="font-mono">{formatAns(q, d.user_answer) || "—"}</span></div>
                        {d.correct_answers && (
                          <div className="text-green-700">Correct: <span className="font-mono">{formatAns(q, d.correct_answers)}</span></div>
                        )}
                        {q.explanation && <div className="text-zinc-500 italic">{q.explanation}</div>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Marks</div>
                      <div className={`font-mono-tab font-bold ${d.marks_obtained > 0 ? "text-green-700" : d.marks_obtained < 0 ? "text-red-700" : "text-zinc-600"}`}>
                        {d.marks_obtained > 0 ? "+" : ""}{d.marks_obtained}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </ReviewGate>

      {/* Review request modal (student writes an optional reason) */}
      {reasonModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="review-request-modal" onClick={() => setReasonModalOpen(false)}>
          <div className="bg-white max-w-md w-full border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-zinc-200">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Detailed review</div>
              <div className="font-display text-lg font-bold mt-1">Request answer review</div>
              <div className="text-xs text-zinc-500 mt-1">Your admin / examiner will decide whether to reveal the correct answers &amp; explanations for this attempt.</div>
            </div>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Reason (optional)</div>
              <textarea
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                rows={4}
                placeholder="e.g. I believe my answer for Q3 is also acceptable — please check."
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="review-request-reason"
                maxLength={1000}
              />
              <div className="text-[10px] text-zinc-500 mt-1 text-right">{reasonText.length}/1000</div>
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2">
              <button onClick={() => setReasonModalOpen(false)} className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900" data-testid="review-request-cancel">Cancel</button>
              <button
                onClick={submitReviewRequest}
                disabled={reqBusy}
                className="px-4 py-2 bg-zinc-900 text-white text-sm hover:bg-zinc-800 disabled:opacity-50"
                data-testid="review-request-submit"
              >
                {reqBusy ? "Sending…" : "Send request"}
              </button>
            </div>
          </div>
        </div>
      )}
      <PlagiarismCheckModal
        open={!!plagQ}
        onOpenChange={(v) => !v && setPlagQ(null)}
        examId={attempt?.exam_id}
        questionId={plagQ}
      />
    </div>
  );
}

function SummaryCell({ label, value, colorClass }) {
  return (
    <div className={`p-6 border-r border-zinc-200 last:border-r-0 ${colorClass || ""}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">{label}</div>
      <div className="font-display text-2xl font-bold tabular">{value}</div>
    </div>
  );
}

function formatAns(q, arr) {
  if (!arr || arr.length === 0) return "";
  if (q.type === "short_answer") return arr[0];
  return arr.map((s) => q.options?.[parseInt(s, 10)] ?? s).join(", ");
}


function ReviewGate({ isStaff, reviewReq, onRequest, children }) {
  // Staff always see the full review.
  if (isStaff) return children;
  const status = reviewReq?.status;
  if (status === "approved") return children;
  return (
    <div className="border border-zinc-200 bg-zinc-50 p-6" data-testid="review-gate">
      <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Detailed review</div>
      {!reviewReq && (
        <>
          <div className="text-sm text-zinc-700 mb-3">
            The question-by-question breakdown (correct answers &amp; explanations) is available <span className="font-semibold">only after your admin approves your request</span>.
          </div>
          <button
            onClick={onRequest}
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-zinc-800"
            data-testid="request-review-btn"
          >
            Request detailed review
          </button>
        </>
      )}
      {status === "pending" && (
        <div className="border-l-4 border-amber-500 pl-4 py-2 bg-amber-50" data-testid="review-status-pending">
          <div className="text-xs uppercase tracking-wider text-amber-800 font-semibold">Pending approval</div>
          <div className="text-sm text-zinc-700 mt-1">
            Your request was sent on {new Date(reviewReq.created_at).toLocaleString()}. You&apos;ll see the detailed review here once your admin approves.
          </div>
          {reviewReq.reason && (
            <div className="text-xs text-zinc-500 mt-2 italic">Your note: &ldquo;{reviewReq.reason}&rdquo;</div>
          )}
        </div>
      )}
      {status === "rejected" && (
        <div className="border-l-4 border-red-500 pl-4 py-2 bg-red-50" data-testid="review-status-rejected">
          <div className="text-xs uppercase tracking-wider text-red-800 font-semibold">Request rejected</div>
          <div className="text-sm text-zinc-700 mt-1">
            Rejected by <span className="font-semibold">{reviewReq.decided_by_name}</span> on {new Date(reviewReq.decided_at).toLocaleString()}.
          </div>
          {reviewReq.rejection_reason && (
            <div className="text-sm text-red-900 mt-2">
              <span className="font-semibold">Reason:</span> {reviewReq.rejection_reason}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
